import React, { useEffect, useState } from 'react';
import {
    Typography,
    Button,
    Snackbar,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Card,
    CardContent,
    CardActions,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    Collapse,
    Box,
    TablePagination
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons';
import reviewStore from '../../stores/license/licenseReviewStore';
import { observer } from 'mobx-react-lite';
import LinearProgressWithLabel from '../../components/LinearProgress/LinearProgressWithLabel';

const ManageReview: React.FC = observer(() => {
    const [confirmCreateOpen, setConfirmCreateOpen] = useState(false);
    const [confirmCompleteOpen, setConfirmCompleteOpen] = useState<string | null>(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [openRows, setOpenRows] = useState<{ [key: string]: boolean }>({});
    const [previewPage, setPreviewPage] = useState(0);
    const [previewRowsPerPage, setPreviewRowsPerPage] = useState(10);

    useEffect(() => {
        reviewStore.fetchDepartments();
        reviewStore.fetchAllActiveReviews();
    }, []);

    const handlePreviewPageChange = (_: unknown, newPage: number) => {
        setPreviewPage(newPage);
    };

    const handlePreviewRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPreviewRowsPerPage(parseInt(event.target.value, 10));
        setPreviewPage(0);
    };

    const handleToggle = (managerEmail: string) => {
        setOpenRows(prev => ({ ...prev, [managerEmail]: !prev[managerEmail] }));
    };

    const handleMarkAsCompleted = async () => {
        if (confirmCompleteOpen) {
            await reviewStore.markReviewProcessAsCompleted(confirmCompleteOpen);
            setConfirmCompleteOpen(null);
        }
    };

    const handleCreateReviews = async () => {
        await reviewStore.createReviews();
        setConfirmCreateOpen(false);
    };

    const handleCloseSnackbar = () => {
        reviewStore.setNotificationMessage(null);
    };

    const handleDepartmentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        reviewStore.setSelectedDepartment(event.target.value as string);
    };

    const handlePreviewClick = async () => {
        if (reviewStore.selectedDepartmentId) {
            reviewStore.setPreviewData([]);
            setPreviewPage(0);
            setOpenRows({});
            await reviewStore.fetchLicenseDataPreview(reviewStore.selectedDepartmentId);
            setPreviewDialogOpen(true);
        }
    };
    
    const calculateProgress = (completed: number, total: number) => {
        if (total === 0) return 0;
        return (completed / total) * 100;
    };

    const canCreateReview = (departmentName: string | null) => {
        if (!departmentName) return false;
        return !reviewStore.departmentHasActiveReview(departmentName);
    };

    return (
        <div>
            <Typography variant="h4" component="h1" gutterBottom>
                Manage License Reviews
            </Typography>
            <Typography variant="body1" gutterBottom>
                This page is used to manage the license review process for each department.
                Select a department to create new reviews or check the progress of ongoing reviews.
            </Typography>
            <hr />

            {/* Top Section: Create Reviews */}
            <Paper style={{ padding: 16, marginBottom: 24 }}>
                <Typography variant="h6" gutterBottom>
                    Create New Review Process
                </Typography>

                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <FormControl variant="outlined" style={{ minWidth: 250, marginBottom: 16 }}>
                        <InputLabel id="department-select-label">Select Department</InputLabel>
                        <Select
                            labelId="department-select-label"
                            value={reviewStore.selectedDepartmentId || ''}
                            onChange={handleDepartmentChange}
                            label="Select Department"
                            disabled={reviewStore.departments.length === 0 || reviewStore.loading}
                        >
                            {reviewStore.departments.map(dept => (
                                <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handlePreviewClick}
                        disabled={!reviewStore.selectedDepartmentId}
                        style={{ marginBottom: 16 }}
                    >
                        {reviewStore.loadingPreviewData ? (
                            <CircularProgress size={24} />
                        ) : (
                            'Preview Data'
                        )}
                    </Button>
                </div>

                <div>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setConfirmCreateOpen(true)}
                        disabled={
                            !reviewStore.selectedDepartmentId ||
                            reviewStore.createReviewsLoading ||
                            !canCreateReview(reviewStore.selectedDepartmentName)
                        }
                    >
                        Create Reviews for {reviewStore.departments.find(d => d.id === reviewStore.selectedDepartmentId)?.name || 'Selected Department'}
                    </Button>

                    {reviewStore.selectedDepartmentId && !canCreateReview(reviewStore.selectedDepartmentName) && (
                        <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
                            This department already has an active review process
                        </Typography>
                    )}
                </div>

                {reviewStore.createReviewsLoading && (
                    <CircularProgress size={24} style={{ marginLeft: 16, verticalAlign: 'middle' }} />
                )}
            </Paper>

            {/* Bottom Section: Active Reviews */}
            <Paper style={{ padding: 16 }}>
                <Typography variant="h6" gutterBottom>
                    Active Review Processes
                </Typography>

                {reviewStore.loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                        <CircularProgress />
                    </div>
                ) : reviewStore.fetchError ? (
                    <Typography variant="body1" color="error">
                        {reviewStore.fetchError}
                    </Typography>
                ) : reviewStore.activeReviews.length === 0 ? (
                    <Typography variant="body1" color="textSecondary">
                        No active review processes found
                    </Typography>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {reviewStore.activeReviews.map((activeReview) => (
                            <Card key={activeReview.id}>
                                <CardContent>
                                    {/* More prominent department display */}
                                    <div style={{ marginBottom: 12, borderBottom: '1px solid #e0e0e0', paddingBottom: 8 }}>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Department:
                                        </Typography>
                                        <Typography variant="h5" color="primary">
                                            {activeReview.department}
                                        </Typography>
                                    </div>

                                    <Typography variant="body2" gutterBottom>
                                        Start Date: {new Date(activeReview.startDate).toLocaleDateString()}
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                        Progress: {activeReview.reviewsCompleted} of {activeReview.totalReviews} reviews completed
                                    </Typography>
                                    <LinearProgressWithLabel
                                        value={calculateProgress(
                                            activeReview.reviewsCompleted,
                                            activeReview.totalReviews
                                        )}
                                    />
                                </CardContent>
                                <CardActions>
                                    <Button
                                        color="primary"
                                        onClick={() => setConfirmCompleteOpen(activeReview.id)}
                                        disabled={activeReview.reviewsCompleted < activeReview.totalReviews}
                                    >
                                        Mark as Completed
                                    </Button>
                                    {activeReview.reviewsCompleted < activeReview.totalReviews && (
                                        <Typography variant="caption" color="textSecondary">
                                            All reviews must be completed first
                                        </Typography>
                                    )}
                                </CardActions>
                            </Card>
                        ))}
                    </div>
                )}
            </Paper>

            {/* Dialogs */}
            <Dialog
                open={confirmCreateOpen}
                onClose={() => setConfirmCreateOpen(false)}
            >
                <DialogTitle>Confirm Create Reviews</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to create new reviews for {reviewStore.departments.find(d => d.id === reviewStore.selectedDepartmentId)?.name}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCreateOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleCreateReviews} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!confirmCompleteOpen}
                onClose={() => setConfirmCompleteOpen(null)}
            >
                <DialogTitle>Confirm Mark as Completed</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to mark the review process for {reviewStore.activeReviews.find(ar => ar.id === confirmCompleteOpen)?.department} as completed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmCompleteOpen(null)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleMarkAsCompleted} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    License Data Preview for {reviewStore.departments.find(d => d.id === reviewStore.selectedDepartmentId)?.name || 'Selected Department'}
                </DialogTitle>
                <DialogContent dividers>
                    {reviewStore.loadingPreviewData ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                            <CircularProgress />
                        </div>
                    ) : reviewStore.previewError ? (
                        <Typography variant="body1" color="error">
                            {reviewStore.previewError}
                        </Typography>
                    ) : reviewStore.previewData.length === 0 ? (
                        <Typography variant="body1" color="textSecondary">
                            No license data found for this department
                        </Typography>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell />
                                            <TableCell>Manager Name</TableCell>
                                            <TableCell>Manager Email</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reviewStore.previewData
                                            .slice(previewPage * previewRowsPerPage, previewPage * previewRowsPerPage + previewRowsPerPage)
                                            .map((managerData, index) => (
                                                <React.Fragment key={index}>
                                                    <TableRow>
                                                        <TableCell>
                                                            <IconButton size="small" onClick={() => handleToggle(managerData.managerEmail)}>
                                                                {openRows[managerData.managerEmail] ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>{managerData.managerName}</TableCell>
                                                        <TableCell>{managerData.managerEmail}</TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell colSpan={3} style={{ paddingBottom: 0, paddingTop: 0 }}>
                                                            <Collapse in={openRows[managerData.managerEmail]} timeout="auto" unmountOnExit>
                                                                <Box margin={2}>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Name</TableCell>
                                                                                <TableCell>Email</TableCell>
                                                                                <TableCell>Title</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {(managerData.employees || []).map((employee, empIndex) => (
                                                                                <TableRow key={empIndex}>
                                                                                    <TableCell>{employee.name}</TableCell>
                                                                                    <TableCell>{employee.email}</TableCell>
                                                                                    <TableCell>{employee.title}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={reviewStore.previewData.length}
                                page={previewPage}
                                onPageChange={handlePreviewPageChange}
                                rowsPerPage={previewRowsPerPage}
                                onRowsPerPageChange={handlePreviewRowsPerPageChange}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialogOpen(false)} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={!!reviewStore.notificationMessage}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={reviewStore.notificationSeverity}>
                    {reviewStore.notificationMessage}
                </Alert>
            </Snackbar>
        </div>
    );
});

export default ManageReview;